import { Form, Formik } from "formik";
import { computed, observable } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { compose } from "recompose";
import * as Yup from "yup";

import { AreaTypeDropdownField } from "nvent-web/components/AreaTypeDropdownField/AreaTypeDropdownField";
import FloorConstructionField from "nvent-web/components/FloorConstructionField/FloorConstructionField";
import FloorFinishField from "nvent-web/components/FloorFinishField/FloorFinishField";
import { CheckboxField } from "nvent-web/components/form/CheckboxField";
import RoomAreas from "nvent-web/components/RoomAreas/RoomAreas";
import SubmitCancelButtons from "nvent-web/components/SubmitCancelButtons";
import { buildMessage } from "nvent-web/i18n/yup";
import { RequiredFieldLabel } from "nvent-web/scenes/Auth/Register/components/common";
import AppStore from "nvent-web/stores/App";
import { RoomsStore } from "nvent-web/stores/Rooms";
import { UserStore } from "nvent-web/stores/User";
import { BuildingType } from "nvent-web/types/BuildingType";
import { RawSelectionGuideFormValues, SelectionGuideFormValues } from "nvent-web/types/SelectionGuideFormValues";

import style from "./RoomDetailsStepForm.module.scss";

interface RoomDetailsStepFormProps {
  onCancel: () => void;
  initialValues: SelectionGuideFormValues;
  handleSubmit: (formValues: SelectionGuideFormValues) => void;
  isSubmitting: boolean;
  buildingType: BuildingType | null;
}
interface RoomDetailsStepFormInnerProps extends RoomDetailsStepFormProps, InjectedIntlProps {
  app: AppStore;
  rooms: RoomsStore;
  user: UserStore;
}

const quickHeatingFloorFinishes = new Set(["tile", "natural_stone"]);
const quickHeatingCountriesBlacklist = new Set(["BY", "KZ", "RU", "UA"]);

const areaSchema = Yup.number()
  .required()
  .transform((_, value) => (value === undefined || value === "" ? null : Number(value.replace(",", "."))))
  .typeError(buildMessage("error.number.positive"))
  .positive();

class RoomDetailsStepFormInner extends React.Component<RoomDetailsStepFormInnerProps> {
  schema = Yup.object<SelectionGuideFormValues>({
    area: areaSchema,
    installableArea: areaSchema.when("area", (area: number, schema: Yup.NumberSchema) =>
      schema.max(area, buildMessage("room.error.installableArea.max"))
    ),
    floorConstruction: Yup.string()
      .nullable()
      .required(),
    floorFinish: Yup.string()
      .nullable()
      .required(),
    quickHeating: Yup.boolean(),
    areaType: Yup.string()
      .nullable()
      .required(),
  });

  @observable
  private floorFinish: string = this.props.initialValues.floorFinish || "";

  @computed
  private get quickHeatingAvailable() {
    const { countryCode } = this.props.user.companyAddress;

    return (
      !quickHeatingCountriesBlacklist.has(countryCode) &&
      this.props.buildingType === "new_building" &&
      quickHeatingFloorFinishes.has(this.floorFinish)
    );
  }

  render() {
    const { onCancel, initialValues, isSubmitting } = this.props;

    const rawInitialValues: RawSelectionGuideFormValues = {
      area: initialValues.area ? initialValues.area.toString() : "",
      installableArea: initialValues.installableArea ? initialValues.installableArea.toString() : "",
      floorConstruction: initialValues.floorConstruction || "",
      floorFinish: initialValues.floorFinish || "",
      quickHeating: initialValues.quickHeating || false,
      areaType: initialValues.areaType,
    };

    return (
      <>
        <Formik validationSchema={this.schema} initialValues={rawInitialValues} onSubmit={this.handleSubmit}>
          {({ initialValues: { floorFinish } }) => (
            <Form noValidate={true} className={style.wrapper}>
              <div className={style.column}>
                <RoomAreas required />
              </div>

              <div className={style.column}>
                <FloorConstructionField required />

                <FloorFinishField required onChange={this.handleFloorFinishChange} initialValue={floorFinish} />
              </div>

              <div className={style.column}>
                <AreaTypeDropdownField required />
              </div>

              {this.quickHeatingAvailable && (
                <div className={style.column}>
                  <CheckboxField
                    name="quickHeating"
                    label={<FormattedMessage id="selectionGuide.quickHeating" />}
                    className={{
                      wrapper: style.quickHeatingCheckbox,
                    }}
                  />
                </div>
              )}
              <div className={style.columnFullWidth}>
                <RequiredFieldLabel />
              </div>
              <div className={style.buttons}>
                <div className={style.buttonsColumn}>
                  <div className={style.actions}>
                    <SubmitCancelButtons
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      onCancel={onCancel}
                      submitLabel={<FormattedMessage id="actions.next" />}
                    />
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </>
    );
  }

  private handleFloorFinishChange = (floorFinish: string) => {
    this.floorFinish = floorFinish;
  };

  private handleSubmit = (rawValues: RawSelectionGuideFormValues) => {
    this.props.handleSubmit(
      this.schema.cast({
        ...rawValues,
        quickHeating: this.quickHeatingAvailable ? rawValues.quickHeating : undefined,
      })
    );
  };
}

const RoomDetailsStepForm = compose<RoomDetailsStepFormInnerProps, RoomDetailsStepFormProps>(
  injectIntl,
  inject("app", "rooms", "user"),
  observer
)(RoomDetailsStepFormInner);

export default RoomDetailsStepForm;
